<!-- src/components/admin/PaidBookingsChart.vue -->

<template>
    <div>
      <!-- Row: Year Select & Refresh -->
      <div class="d-flex align-center mb-4">
        <v-select
          v-model="selectedYear"
          :items="availableYears"
          label="Select Year"
          class="mr-4"
          @change="updateChartAndStats"
        ></v-select>
  
        <v-btn color="primary" class="mr-2" @click="fetchPaidBookings">
          Refresh Data
        </v-btn>
      </div>
  
      <!-- Paid Bookings Chart -->
      <h2 class="mb-4">Paid Bookings Chart</h2>
      <v-chart
        autoresize
        class="chart"
        :option="barOption"
      />
  
      <!-- Stats card (Paid only) -->
      <v-card class="mt-15 mb-15 pa-4 stats-card">
        <div class="d-flex flex-row flex-wrap justify-space-between align-center">
          <!-- Total Rents Box (Paid only) -->
          <div class="stat-box pa-4 ma-2 text-center">
            <v-icon color="green" size="36">mdi-cash</v-icon>
            <h3 class="mt-2">Total Paid Rents ({{ selectedYear }})</h3>
            <p class="text-h6 font-weight-bold mb-0">
              <span v-if="totalPaidRent !== null">
                {{ totalPaidRent }} OMR
              </span>
            </p>
          </div>
  
          <!-- Total Booked Days Box (Paid only) -->
          <div class="stat-box pa-4 ma-2 text-center">
            <v-icon color="blue" size="36">mdi-calendar</v-icon>
            <h3 class="mt-2">Total Paid Days ({{ selectedYear }})</h3>
            <p class="text-h6 font-weight-bold mb-0">
              <span v-if="totalPaidDays !== null">
                {{ totalPaidDays }} days
              </span>
            </p>
          </div>
        </div>
      </v-card>
    </div>
  </template>
  
  <script>
  import apiService from '@/services/apiService.js';
  import VChart from "vue-echarts";
  import { use } from "echarts/core";
  import { CanvasRenderer } from "echarts/renderers";
  import { BarChart } from "echarts/charts";
  import {
    TitleComponent,
    TooltipComponent,
    LegendComponent,
    GridComponent
  } from "echarts/components";
  
  use([
    CanvasRenderer,
    BarChart,
    TitleComponent,
    TooltipComponent,
    LegendComponent,
    GridComponent
  ]);
  
  export default {
    name: "PaidBookingsChart",
    components: { VChart },
    props: {
      token: {
        type: String,
        required: true
      }
    },
    data() {
      return {
        paidBookings: [],
        availableYears: [],
        selectedYear: null,
        errorMsg: null,
  
        // Chart
        barOption: {
          title: { text: "Paid Bookings Chart", left: "center" },
          tooltip: { trigger: "axis" },
          legend: { data: ["Total Price", "Booked Days"], bottom: 0 },
          xAxis: [{ type: "category", data: [] }],
          yAxis: [{ type: "value", name: "Number / Price" }],
          series: [
            { name: "Total Price", type: "bar", data: [], barWidth: "35%" },
            { name: "Booked Days", type: "bar", data: [], barWidth: "35%" },
          ],
        },
        totalPaidRent: 0,
        totalPaidDays: 0,
      };
    },
    methods: {
      fetchPaidBookings() {
        this.errorMsg = null;
        apiService.fetchPaidBookings(this.token)
          .then(res => {
            this.paidBookings = res.data;
            this.buildAvailableYears();
            this.updateChartAndStats();
          })
          .catch(err => {
            this.$emit('error', err.response?.data?.error || "Failed to fetch paid bookings.");
          });
      },
      buildAvailableYears() {
        const yearSet = new Set();
        for (const b of this.paidBookings) {
          if (!b.start_date) continue;
          const d = new Date(b.start_date + "T00:00:00");
          if (!isNaN(d.getTime())) {
            yearSet.add(d.getFullYear());
          }
        }
        const arr = Array.from(yearSet).sort((a, b) => a - b);
        this.availableYears = arr;
        if (!this.selectedYear && arr.length) {
          this.selectedYear = arr[arr.length - 1];
        }
        this.updateChartAndStats();
      },
      updateChartAndStats() {
        const monthsPrice = new Array(12).fill(0);
        const monthsDays = new Array(12).fill(0);
        let sumPrice = 0;
        let sumDays = 0;
        for (const b of this.paidBookings) {
          if (!b.start_date || !b.total_price) continue;
          const d = new Date(b.start_date + "T00:00:00");
          if (isNaN(d.getTime())) continue;
          if (this.selectedYear && d.getFullYear() !== this.selectedYear) continue;
          const m = d.getMonth();
          const priceVal = Number(b.total_price) || 0;
          const dayVal = Number(b.numofdays) || 0;
          monthsPrice[m] += priceVal;
          monthsDays[m] += dayVal;
          sumPrice += priceVal;
          sumDays += dayVal;
        }
        this.totalPaidRent = sumPrice;
        this.totalPaidDays = sumDays;
        this.barOption = {
          ...this.barOption,
          xAxis: [{ type: "category", data: this.getFilteredMonths() }],
          series: [
            { ...this.barOption.series[0], data: monthsPrice },
            { ...this.barOption.series[1], data: monthsDays },
          ],
        };
      },
      getFilteredMonths() {
        return [
          "Jan", "Feb", "Mar", "Apr", "May", "Jun",
          "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
        ];
      },
    },
    watch: {
      selectedYear() {
        this.updateChartAndStats();
      }
    },
    mounted() {
      this.fetchPaidBookings();
    }
  };
  </script>
  
  <style scoped>
  .chart {
    padding-top: 30px;
    height: 400px;
    width: 100%;
  }
  
  .stats-card {
    background-color: #f5f5f5;
    border-radius: 10px;
  }
  
  .stat-box {
    background-color: #ffffff;
    border: 1px solid #e1e1e1;
    border-radius: 8px;
    width: 280px;
  }
  
  .stat-box h3 {
    font-size: 18px;
    margin: 8px 0;
  }
  
  .stat-box p {
    font-size: 20px;
    margin: 0;
  }
  
  .text-center {
    text-align: center;
  }
  
  .mb-4 {
    margin-bottom: 16px;
  }
  
  .mt-15 {
    margin-top: 15px;
  }
  
  .mb-15 {
    margin-bottom: 15px;
  }
  
  .mr-4 {
    margin-right: 16px;
  }
  
  .mr-2 {
    margin-right: 8px;
  }
  </style>
  